import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/error';
import { allocateMachineRequest, listMachinesRequest } from '@/services/machines';
import type { AllocateMachineBody, MachineListItem } from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';

/**
 * Plan 11-03 — AllocateMachineFromSiteDialog
 *
 * Site-side counterpart to the machine-side `AllocateMachineDialog`. Triggered
 * from the SITE-105 tab on Site Detail. The dialog:
 *
 *  - Lists UNALLOCATED machines (`status=unallocated`) — `enabled: open` defers
 *    the fetch until the dialog actually opens (T-11-23 — only show machines
 *    the caller's `machines.read` scope grants).
 *  - User picks a machine + allocation date (defaults today) + optional notes
 *    (max 500 chars).
 *  - Submit fires `allocateMachineRequest(machineId, { siteId, allocatedAt,
 *    notes })`. On 204 the parent's `onSuccess` runs (invalidates queries) and
 *    the dialog closes.
 *  - Empty unallocated-machines list: show informational copy with
 *    `data-testid="amsd-empty"` and disable the submit button.
 */

export interface AllocateMachineFromSiteDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly siteId: number;
  readonly onSuccess: () => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AllocateMachineFromSiteDialog({
  open,
  onOpenChange,
  siteId,
  onSuccess,
}: AllocateMachineFromSiteDialogProps): JSX.Element {
  const [machineId, setMachineId] = useState<number | null>(null);
  const [allocatedAt, setAllocatedAt] = useState<string>(todayIso());
  const [notes, setNotes] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);

  // Reset state when the dialog opens.
  useEffect(() => {
    if (open) {
      setMachineId(null);
      setAllocatedAt(todayIso());
      setNotes('');
      setErrorMessage(null);
      setSubmitAttempted(false);
    }
  }, [open]);

  const machinesQuery = useQuery<PaginatedResult<MachineListItem>>({
    queryKey: ['machines', { status: 'unallocated' as const }],
    queryFn: () => listMachinesRequest({ status: 'unallocated', pageSize: 100 }),
    enabled: open,
  });

  const allocateMutation = useMutation<undefined, ApiError, { body: AllocateMachineBody }>({
    mutationFn: async ({ body }) => {
      if (machineId === null) throw new Error('No machine selected');
      await allocateMachineRequest(machineId, body);
      return undefined;
    },
    onSuccess: () => {
      onSuccess();
      onOpenChange(false);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
    },
  });

  const machineOptions = machinesQuery.data?.items ?? [];
  const noUnallocatedMachines = !machinesQuery.isLoading && machineOptions.length === 0;

  function handleSubmit(): void {
    setSubmitAttempted(true);
    if (machineId === null) {
      setErrorMessage('Pick an unallocated machine.');
      return;
    }
    if (allocatedAt === '' || Number.isNaN(Date.parse(allocatedAt))) {
      setErrorMessage('Pick a valid allocation date.');
      return;
    }
    if (notes.length > 500) {
      setErrorMessage('Notes must be 500 characters or fewer.');
      return;
    }
    setErrorMessage(null);
    const body: AllocateMachineBody = {
      siteId,
      allocatedAt: new Date(`${allocatedAt}T00:00:00Z`).toISOString(),
      notes: notes.trim() === '' ? null : notes.trim(),
    };
    allocateMutation.mutate({ body });
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="allocate-machine-from-site-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Allocate machine to this site</AlertDialogTitle>
          <AlertDialogDescription>
            Pick an unallocated machine to allocate to this site. The new allocation row opens
            immediately.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          {noUnallocatedMachines ? (
            <Alert variant="default" data-testid="amsd-empty">
              <AlertTitle>No unallocated machines available</AlertTitle>
              <AlertDescription>
                Create a new machine first, or deallocate one from another site.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="amsd-machine">Machine</Label>
              <Select
                value={machineId !== null ? String(machineId) : ''}
                onValueChange={(v) => {
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) setMachineId(n);
                }}
                disabled={machinesQuery.isLoading || allocateMutation.isPending}
              >
                <SelectTrigger id="amsd-machine" data-testid="amsd-machine-trigger">
                  <SelectValue placeholder="Select a machine…" />
                </SelectTrigger>
                <SelectContent>
                  {machineOptions.map((m) => (
                    <SelectItem
                      key={m.id}
                      value={String(m.id)}
                      data-testid={`amsd-machine-option-${String(m.id)}`}
                    >
                      {m.name} (Code: {m.code}, Type: {m.machineTypeName}
                      {m.vehicleNumber ? `, Vehicle: ${m.vehicleNumber}` : ''})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {submitAttempted && machineId === null && (
                <p className="text-xs text-destructive" data-testid="amsd-machine-error">
                  Pick a machine.
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="amsd-allocated-at">Allocation date</Label>
            <Input
              id="amsd-allocated-at"
              type="date"
              value={allocatedAt}
              onChange={(e) => {
                setAllocatedAt(e.target.value);
              }}
              disabled={allocateMutation.isPending || noUnallocatedMachines}
              data-testid="amsd-allocated-at"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="amsd-notes">Notes (optional, max 500 chars)</Label>
            <Textarea
              id="amsd-notes"
              rows={3}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              disabled={allocateMutation.isPending || noUnallocatedMachines}
              maxLength={500}
              data-testid="amsd-notes"
            />
          </div>
        </div>

        {errorMessage != null && errorMessage !== '' && (
          <Alert variant="destructive" data-testid="amsd-error">
            <AlertTitle>Could not allocate</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={allocateMutation.isPending}
            data-testid="amsd-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={allocateMutation.isPending || noUnallocatedMachines}
            data-testid="amsd-submit"
          >
            {allocateMutation.isPending ? 'Allocating…' : 'Allocate'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
